import { Button } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { Ssm5PanelMessage } from './ssm5-panel-message';

/** Props for {@link Ssm5FreezeFramesFailure}. */
export type Ssm5FreezeFramesFailureProps = {
    /** Whether the connection is open, so a failed load can be read again. */
    canRetry: boolean;
    /** Whether the data is no longer stored. */
    isGone: boolean;
    /** Reads the data again. */
    onRetry: () => void;
    /** Returns to the event detail. */
    onReturnToDtcs: () => void;
};

/** The messages and actions for data that is gone or failed to load. */
const FAILURES = {
    gone: {
        action: 'ssm5:ffd.gone.back',
        description: 'ssm5:ffd.gone.description',
        icon: 'retry-refresh',
        style: ssm5ControlStyles.primaryButton,
        title: 'ssm5:ffd.gone.title',
        variant: 'ghost-blue',
    },
    loadFailed: {
        action: 'ssm5:ffd.loadFailed.retry',
        description: 'ssm5:ffd.loadFailed.description',
        icon: 'retry-refresh',
        style: ssm5ControlStyles.outlineButton,
        title: 'ssm5:ffd.loadFailed.title',
        variant: 'primary-blue-outline',
    },
} as const;

/**
 * The reason a freeze frame read failed and the action available to the technician.
 *
 * @param props - {@link Ssm5FreezeFramesFailureProps}
 * @returns the failure message and its available action
 */
export function Ssm5FreezeFramesFailure({ canRetry, isGone, onRetry, onReturnToDtcs }: Ssm5FreezeFramesFailureProps) {
    const { t } = useTranslation();

    const kind = isGone ? 'gone' : 'loadFailed';
    const message = FAILURES[kind];
    const handlers = { gone: onReturnToDtcs, loadFailed: canRetry ? onRetry : undefined };
    const onPress = handlers[kind];
    const hasAction = onPress !== undefined;

    return (
        <Ssm5PanelMessage
            action={
                hasAction && (
                    <Button
                        onPress={onPress}
                        style={message.style}
                        title={t(message.action)}
                        titleStyle={ssm5TextStyles.primaryButtonTitle}
                        variant={message.variant}
                    />
                )
            }
            description={t(message.description)}
            icon={message.icon}
            title={t(message.title)}
            variant="page"
        />
    );
}
